import {Component, EventEmitter, inject, Input, OnInit, OnChanges, SimpleChanges, Output, OnDestroy} from '@angular/core';
import {DxPieChartModule} from "devextreme-angular";
import {Subscription, Subject, EMPTY, Observable} from 'rxjs';
import {distinctUntilChanged, switchMap, catchError} from 'rxjs/operators';
import {IMyScoreShiftQuery, ScoreDto, ScreenService, ShiftType} from "@services";
import {PercentPipe} from "@angular/common";
import {DxChartTypes} from "devextreme-angular/ui/chart";
import {CapitalCasePipe} from "../../../pipes/capital-case.pipe";
import {hasScoreData, NoData, noDataColor, noDataRing, scoreCenterLabel} from "./score-center";

export type ChartDataPoint = {
    argument: string;
    value: number;
}

@Component({
    selector: 'app-doughnut-chart',
    imports: [DxPieChartModule],
    providers: [CapitalCasePipe],
    templateUrl: './doughnut-chart.component.html',
    styleUrl: './doughnut-chart.component.scss',
})
export class DoughnutChartComponent implements OnInit, OnChanges, OnDestroy {
    private screen = inject(ScreenService);
    private capitalCase = inject(CapitalCasePipe)

    @Input() from: Date;
    @Input() to: Date;
    @Input() employeeId: string | null = null;
    @Input() shiftType: ShiftType | null = null;
    @Input() centerText: string;
    @Input() responsive: boolean = true;
    @Input() formatMode: 'percent' | 'decimal' = 'percent';
    @Input() useCapitalCaseFormatting: boolean = true;
    @Input() legendRowCount: number | undefined = undefined;
    @Input() legendExclude: string[] = [];
    @Input() noDataExclude: string[] = [];
    // For scores that are valid without totals, e.g. an SLA blank upload scores 100%.
    @Input() scoreCountsAsData = false;
    @Input() loadData: (query: IMyScoreShiftQuery) => Observable<ScoreDto>;
    @Input() palette: Record<string, string> = {};
    @Output() loaded = new EventEmitter<ChartDataPoint[]>();

    protected items: ChartDataPoint[] = [];
    protected chartItems: ChartDataPoint[] = [];
    protected hasData = false;
    protected chartPalette: Record<string, string> = {};
    private params$ = new Subject<IMyScoreShiftQuery>();
    private dataSubscription: Subscription | null = null;
    private percentPipe = new PercentPipe('en-US');

    centerValue: number | null | undefined;
    protected hasLoaded = false;

    ngOnInit(): void {
        this.dataSubscription = this.params$.pipe(
            distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
            switchMap(params => {
                if (params.from && this.loadData) {
                    return this.loadData(params).pipe(
                        catchError(() => {
                            return this.empty()
                        })
                    );
                } else {
                    return this.empty()
                }
            })
        ).subscribe(score => {
            this.hasLoaded = true;
            this.centerValue = score.avgScorePercent ?? null;

            this.chartPalette = { ...this.palette };
            if (score.pallet) {
                Object.entries(score.pallet).forEach(([key, colorClass]) => {
                    const varName = `--bs-${String(colorClass).toLowerCase()}`;
                    const colorValue = getComputedStyle(document.body).getPropertyValue(varName).trim();
                    if (colorValue) {
                        this.chartPalette[key] = colorValue;
                    }
                });
            }

            this.setItems(Object.entries(score.totals ?? {}).map(([key, value]) => ({
                argument: key,
                value: value
            })));

            this.loaded.emit(this.items);
        });

        this.pushParams();
    }

    empty() {
        this.centerValue = null;
        this.setItems([]);
        this.hasLoaded = true;
        this.chartPalette = { ...this.palette };
        this.loaded.emit(this.items);
        return EMPTY;
    }

    get centerLabel(): string {
        return this.hasLoaded ? scoreCenterLabel(this.hasData, this.centerValue, this.formatMode) : '';
    }

    // The compact 220px chart's hole fits about 7 characters at 40px, so longer values shrink to stay inside the ring.
    protected get centerFontSize(): number {
        return this.responsive ? 40 : Math.min(40, Math.floor(280 / this.centerLabel.length));
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.from || changes.to || changes.employeeId || changes.shiftType) {
            this.pushParams();
        }
    }

    ngOnDestroy(): void {
        this.dataSubscription?.unsubscribe();
        this.params$.complete();
    }

    private setItems(items: ChartDataPoint[]): void {
        this.items = items;
        this.hasData = hasScoreData(items, this.noDataExclude) || (this.scoreCountsAsData && this.centerValue != null);
        this.chartItems = this.hasData ? items : noDataRing();
    }

    private pushParams(): void {
        this.params$.next({
            from: this.from,
            to: this.to,
            userId: this.employeeId,
            shiftType: this.shiftType
        });
    }

    customizeTooltip = ({value, percent, argument}: { value: number, percent: number, argument: string }) => ({
        text: `${this.legendLabel(argument)}: ${this.formatMode === 'percent' ? this.percentPipe.transform(percent) : value}`,
    });

    get isLarge() {
        return this.screen.sizes['screen-large'] || this.screen.sizes['screen-medium'];
    }

    protected get legendItems(): ChartDataPoint[] {
        return this.items.filter(item => !this.legendExclude.includes(item.argument));
    }

    protected get sideLegend(): boolean {
        return this.responsive && this.isLarge;
    }

    customizePoint: DxChartTypes.Properties['customizePoint'] = (pointInfo: { argument: string, value: number }) => {
        return {
            color: pointInfo.argument === NoData ? noDataColor : this.legendColor(pointInfo.argument)
        };
    }

    protected legendColor(argument: string): string {
        return this.chartPalette[argument] || 'rgba(100, 100, 100, 0.5)';
    }

    protected legendLabel(argument: string): string {
        return this.useCapitalCaseFormatting ? this.capitalCase.transform(argument) : argument;
    }

    private chart: any;
    protected hiddenArguments = new Set<string>();

    onChartInitialized(e: { component?: any }): void {
        this.chart = e.component;
    }

    private getPoints(argument: string): any[] {
        const series = this.chart?.getAllSeries?.()[0];
        return series?.getPointsByArg?.(argument) ?? [];
    }

    protected onLegendClick(argument: string): void {
        const points = this.getPoints(argument);
        const willHide = points.some(p => p.isVisible());
        points.forEach(p => (willHide ? p.hide() : p.show()));
        if (willHide) {
            this.hiddenArguments.add(argument);
        } else {
            this.hiddenArguments.delete(argument);
        }
    }

    protected onLegendHover(argument: string, hovered: boolean): void {
        this.getPoints(argument).forEach(p => (hovered ? p.hover() : p.clearHover()));
    }
}
